// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — the dealer. Four fittings a round, identical for every player at the table.
//
// NOTHING DEALT EVER CROSSES THE WIRE. Every peer derives the whole run from the one seed frozen
// into rematch.ts's round start, so there is no deal to keep in sync and therefore nothing that can
// desync. That is not discipline, it is construction: the wire has no channel that could carry a
// fitting even if somebody wanted it to.
//
// THE ROUND STREAM IS ADDRESSABLE. Each round consumes a FIXED number of draws from the stream and
// burns whatever it did not use, so round R can be dealt without replaying rounds 0..R-1. A dealer
// whose position depended on how many redraws earlier rounds happened to need would mean a peer that
// joined late — or one recovering a plate from storage after a refresh — deals a different game from
// everyone else while every test stays green.

import { makeRng } from '@ben-gy/game-engine/rng';
import { PIECES, PIECE_IDS, type PieceId } from './pieces';
import { PER_ROUND, type Mode } from './modes';

/** Draws reserved per round. Four fittings plus headroom for the guard redraws below. */
export const DRAWS_PER_ROUND = 12;

const WEIGHT_TOTAL = PIECE_IDS.reduce((n, id) => n + PIECES[id].weight, 0);

/** One weighted draw. Walks the fittings in a fixed order so the mapping is stable forever. */
function drawPiece(r: number): PieceId {
  let t = r * WEIGHT_TOTAL;
  for (const id of PIECE_IDS) {
    t -= PIECES[id].weight;
    if (t < 0) return id;
  }
  return PIECE_IDS[PIECE_IDS.length - 1];
}

const carriesCulvert = (id: PieceId): boolean => id === 'cs' || id === 'ce' || id === 'ct' || id === 'cp' || id === 'bp';
const carriesFilament = (id: PieceId): boolean => id === 'fs' || id === 'fe' || id === 'ft' || id === 'cp' || id === 'bp';
const isTee = (id: PieceId): boolean => id === 'ct' || id === 'ft';

export interface RoundDeal {
  readonly round: number;
  readonly fittings: readonly PieceId[];
  /** How many guard redraws fired. Recorded so the audit can re-derive the deal exactly. */
  readonly redraws: number;
}

/**
 * Deal one round.
 *
 * Three guards, applied in a fixed order against the same stream so they are deterministic:
 *
 *   G1  at least one culvert-capable AND one filament-capable fitting. A round that could only
 *       extend one gauge would strand half the plate through no fault of the player.
 *   G2  at most one bypass. Two bypasses in a round is four open ends you did not ask for.
 *   G3  at most two tees. Tees are the biggest generators of open-end debt in the game.
 *
 * Redraws replace the LAST slot first and give up after three attempts, accepting whatever came out
 * — a guard that could loop forever on an unlucky stream would hang the deal rather than bend it.
 */
export function dealRound(seed: number, round: number, _mode: Mode): RoundDeal {
  const rng = makeRng(seed + round * 7919);
  const draws: number[] = [];
  for (let i = 0; i < DRAWS_PER_ROUND; i++) draws.push(rng());
  let cursor = 0;
  const next = (): PieceId => drawPiece(draws[Math.min(cursor++, DRAWS_PER_ROUND - 1)]);

  const f: PieceId[] = [];
  for (let i = 0; i < PER_ROUND; i++) f.push(next());

  let redraws = 0;
  const bad = (): boolean =>
    !f.some(carriesCulvert) ||
    !f.some(carriesFilament) ||
    f.filter((x) => x === 'bp').length > 1 ||
    f.filter(isTee).length > 2;

  // Replace slot 3, then 2, then 1 — never slot 0, so the round always keeps its opening character.
  for (let attempt = 0; attempt < 3 && bad(); attempt++) {
    f[PER_ROUND - 1 - attempt] = next();
    redraws++;
  }

  return { round, fittings: f, redraws };
}

export interface Run {
  readonly mode: Mode;
  readonly seed: number;
  readonly rounds: readonly RoundDeal[];
}

export function dealRun(mode: Mode, seed: number): Run {
  const rounds: RoundDeal[] = [];
  for (let r = 0; r < mode.rounds; r++) rounds.push(dealRound(seed, r, mode));
  return { mode, seed, rounds };
}

/** A UTC daily seed, so everyone in the world gets the same plate on the same date. */
export function dailySeed(mode: Mode, now = new Date()): number {
  const d = `${now.getUTCFullYear()}-${now.getUTCMonth() + 1}-${now.getUTCDate()}`;
  let h = 2166136261;
  for (const ch of `${d}:${mode.id}`) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
